import React, {useState, useCallback, useEffect} from 'react'
import {useRouter} from 'next/router'
import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'
import AddLineIcon from 'remixicon-react/AddLineIcon'
import BuildingLineIcon from 'remixicon-react/Building4LineIcon'
import UserLineIcon from 'remixicon-react/UserLineIcon'
import BillLineIcon from 'remixicon-react/BillLineIcon'
import cn from 'classnames'

import {theme} from 'src/themes'
import {toAddressInput, isSameAddress, AddressExists} from 'src/utils/checkout'

import {P} from 'src/components/typography'
import {BlockTitle, SpacingWrapper} from 'src/components/layout'
import {ReceiverCard} from 'src/components/checkout'
import AddressForm from 'src/components/account/AddressForm'
import {UnderlineButton, Button} from 'src/components/buttons'
import {Modal, Expandable, DeleteConfirmation} from 'src/components/common'
import {Toggle} from 'src/components/form'

import type {Address, CheckoutError} from 'src/api/saleor/generated'
import {handleCheckoutError, useCheckout} from 'src/context/CheckoutContext'
import {useUser} from 'src/context/UserContext'
import {toast} from 'react-toastify'
import {isEqual, isMatch} from 'lodash'
import {paths} from 'src/utils/paths'
import type {Maybe} from 'src/utils/Maybe'
import {trackStartedCheckout} from 'src/utils/klaviyo'

export interface ErrorProps {
  email?: string
}

const IndentWrapper = styled.div`
  &.indent {
    ${media.md`
      padding-left: 56px;
    `}
  }
`

export default function Receiver(): JSX.Element {
  const {
    user,
    addressData,
    deleteAddress,
    currCustomerAddresses,
    companyId,
  } = useUser()

  const {checkout, updateCheckoutAddresses} = useCheckout()
  const {push} = useRouter()
  const [show, setShow] = useState<boolean>(false)
  const [showBillingForm, setShowBillingForm] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [separateBilling, setSeparateBilling] = useState<boolean>()

  const [addressToDelete, setAddressToDelete] = useState<Address>()
  const [
    selectedShippingAddress,
    setSelectedShippingAddress,
  ] = useState<Address>()
  const [
    selectedBillingAddress,
    setSelectedBillingAddress,
  ] = useState<Address>()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const addressesLeft = currCustomerAddresses?.filter(
    (address) => !isSameAddress(address, selectedShippingAddress),
  )

  const shippingAddressExists = AddressExists(
    currCustomerAddresses,
    checkout?.shippingAddress,
  )

  const billingAddressExists = AddressExists(
    currCustomerAddresses,
    checkout?.billingAddress,
  )

  const onToggleSeparateBilling = useCallback(
    (nextState: boolean) => {
      if (!nextState) {
        setSelectedBillingAddress(selectedShippingAddress)
        setSeparateBilling(!nextState)
      }
      setSeparateBilling(nextState)
      if (nextState && addressesLeft?.length === 1) {
        setSelectedBillingAddress(addressesLeft[0])
      }
    },
    [selectedShippingAddress, addressesLeft],
  )
  // TODO: email update function not working, the post is 200 but the email doesn't update. Will hide for now.
  const onClickNext = useCallback(async () => {
    let shippingAddressErrors: Maybe<CheckoutError[]>
    let billingAddressErrors: Maybe<CheckoutError[]>

    // setErrors({email: ''})
    setIsLoading(true)

    // if (newCheckoutEmail && !validateEmail(newCheckoutEmail)) {
    //   errors.email = 'Netfangið er ekki á réttu formi'
    //   setErrors(errors)
    //   setIsLoading(false)
    //   return
    // }

    // if (newCheckoutEmail && newCheckoutEmail !== checkout?.email) {
    //   await updateCheckoutEmail(newCheckoutEmail)
    // }
    if (!selectedShippingAddress || !selectedBillingAddress) {
      return
    }
    updateCheckoutAddresses(
      toAddressInput(selectedShippingAddress),
      toAddressInput(selectedBillingAddress),
    ).then((res) => {
      shippingAddressErrors =
        res?.data?.checkoutShippingAddressUpdate?.errors ?? []
      billingAddressErrors = res?.data?.checkoutBillingAddressUpdate?.errors

      if (shippingAddressErrors?.length || billingAddressErrors?.length) {
        if (
          isMatch(shippingAddressErrors?.[0], {
            code: 'INVALID',
            field: 'phone',
          })
        ) {
          toast.error('Ógilt símanúmer móttakanda')
        }
        if (
          billingAddressErrors?.[0] &&
          isMatch(billingAddressErrors?.[0], {
            code: 'INVALID',
            field: 'phone',
          })
        ) {
          toast.error('Ógilt símanúmer greiðanda')
        } else {
          handleCheckoutError(shippingAddressErrors)
          handleCheckoutError(billingAddressErrors)
        }
      }
    })

    setIsLoading(false)
    if (!shippingAddressErrors?.length && !billingAddressErrors?.length) {
      push(paths.delivery)
    }
  }, [
    updateCheckoutAddresses,
    selectedShippingAddress,
    selectedBillingAddress,
    push,
  ])

  const onDeleteAddress = useCallback(async () => {
    setIsLoading(true)
    if (addressToDelete?.id) {
      await deleteAddress(addressToDelete.id)
    }
    if (isSameAddress(addressToDelete, selectedShippingAddress)) {
      setSelectedShippingAddress(undefined)
    }
    setIsLoading(false)
    setShowDeleteModal(false)
  }, [
    deleteAddress,
    selectedShippingAddress,
    setSelectedShippingAddress,
    addressToDelete,
  ])

  const onAddShippingAddress = useCallback(
    (address: Address) => {
      setSelectedShippingAddress(address)
      if (!separateBilling) {
        setSelectedBillingAddress(address)
      }
    },
    [setSelectedShippingAddress, setSelectedBillingAddress, separateBilling],
  )

  const onAddBillingAddress = useCallback(
    (address: Address) => {
      setSelectedBillingAddress(address)
    },
    [setSelectedBillingAddress],
  )

  useEffect(() => {
    // Setting initial state
    if (checkout && separateBilling === undefined) {
      if (shippingAddressExists && billingAddressExists) {
        setSeparateBilling(
          !isSameAddress(checkout?.shippingAddress, checkout?.billingAddress),
        )
      }
      if (shippingAddressExists && checkout?.shippingAddress) {
        setSelectedShippingAddress(checkout?.shippingAddress)
      }
      if (billingAddressExists && checkout?.billingAddress) {
        setSelectedBillingAddress(checkout?.billingAddress)
      }
      if (currCustomerAddresses?.length === 1) {
        setSelectedShippingAddress(currCustomerAddresses?.[0])
        setSelectedBillingAddress(currCustomerAddresses?.[0])
      }
    }
  }, [
    checkout,
    separateBilling,
    shippingAddressExists,
    billingAddressExists,
    currCustomerAddresses,
  ])

  useEffect(() => {
    // Turn off separate billing when shipping address is changed
    if (
      selectedShippingAddress &&
      isEqual(selectedShippingAddress, selectedBillingAddress)
    ) {
      setSeparateBilling(false)
    }
  }, [selectedShippingAddress, selectedBillingAddress])

  useEffect(() => {
    if (user && checkout) {
      trackStartedCheckout(
        user,
        checkout?.lines,
        checkout?.totalPrice?.gross?.amount,
      )
    }
  })

  return (
    <>
      <SpacingWrapper spacing={24}>
        {currCustomerAddresses?.map((address) => (
          <ReceiverCard
            key={address.id}
            address={address}
            selected={isEqual(address, selectedShippingAddress)}
            onSelect={() => {
              setSelectedShippingAddress(address)
              if (!separateBilling) {
                setSelectedBillingAddress(address)
              }
            }}
            onDelete={() => {
              setShowDeleteModal(true)
              setAddressToDelete(address)
            }}
            cornerButton={!companyId}
            icon={
              companyId ? (
                <BuildingLineIcon size={30} color={theme.palette.red} />
              ) : (
                <UserLineIcon size={30} color={theme.palette.primary} />
              )
            }
          />
        ))}
        <Modal width="800px" setShow={setShow} show={show}>
          <AddressForm
            formTitle="Móttakandi"
            firstName=""
            lastName=""
            phone=""
            streetAddress1=""
            city=""
            postalCode=""
            setShow={setShow}
            handleAddAddress={onAddShippingAddress}
          />
        </Modal>
        {!companyId && (
          <IndentWrapper
            className={cn({indent: !!addressData?.me?.addresses?.length})}
          >
            <UnderlineButton
              icon={<AddLineIcon color={theme.palette.primary} />}
              text="Nýr móttakandi"
              onClick={() => setShow(true)}
            />
          </IndentWrapper>
        )}
      </SpacingWrapper>
      <SpacingWrapper spacing={50}>
        {!companyId && (
          <>
            <BlockTitle title="Greiðandi" />
            <Toggle
              label="Greiðandi er annar en móttakandi"
              color="primary"
              checked={separateBilling}
              onToggle={onToggleSeparateBilling}
              withGutter
            />
            <SpacingWrapper spacing={24}>
              <Expandable expanded={separateBilling}>
                {addressesLeft?.map((address) => (
                  <ReceiverCard
                    key={address.id}
                    address={address}
                    selected={isSameAddress(address, selectedBillingAddress)}
                    onSelect={() => setSelectedBillingAddress(address)}
                    onDelete={() => {
                      setShowDeleteModal(true)
                      setAddressToDelete(address)
                    }}
                    icon={
                      <BillLineIcon size={30} color={theme.palette.primary} />
                    }
                  />
                ))}
                {!addressesLeft?.length && companyId && (
                  <P withGutter>
                    Það er ekki hægt að velja annan greiðanda fyrir þetta
                    fyrirtæki!
                  </P>
                )}
                {!companyId && (
                  <IndentWrapper
                    className={cn({indent: !!addressesLeft?.length})}
                  >
                    <UnderlineButton
                      icon={<AddLineIcon color={theme.palette.primary} />}
                      text="Nýr greiðandi"
                      onClick={() => setShowBillingForm(true)}
                    />
                  </IndentWrapper>
                )}
                <Modal
                  width="800px"
                  setShow={setShowBillingForm}
                  show={showBillingForm}
                >
                  <AddressForm
                    formTitle="Greiðandi"
                    firstName=""
                    lastName=""
                    phone=""
                    streetAddress1=""
                    city=""
                    postalCode=""
                    setShow={setShowBillingForm}
                    handleAddAddress={onAddBillingAddress}
                  />
                </Modal>
              </Expandable>
            </SpacingWrapper>
          </>
        )}
        <IndentWrapper
          className={cn({
            indent:
              separateBilling &&
              !!addressData?.me?.addresses?.length &&
              !!addressesLeft?.length,
          })}
        >
          {/* <CheckoutEmailUpdate
            setNewCheckoutEmail={setNewCheckoutEmail}
            errors={errors}
          /> */}
          <Button
            withGutter
            color="secondary"
            outlined
            loading={isLoading}
            disabled={
              !selectedShippingAddress ||
              !selectedBillingAddress ||
              (separateBilling &&
                isSameAddress(selectedShippingAddress, selectedBillingAddress))
              //   ||
              // !newCheckoutEmail
              // TODO: Remove !newCheckoutEmail condition when asking for email after login is ready and user email is set as initial value
            }
            onClick={onClickNext}
          >
            Áfram
          </Button>
        </IndentWrapper>
      </SpacingWrapper>
      <Modal show={showDeleteModal} setShow={setShowDeleteModal}>
        <DeleteConfirmation
          title="Ertu viss um að þú viljir eyða upplýsingunum?"
          text="Þú munt ekki geta tekið þessa aðgerð til baka."
          onCancel={() => setShowDeleteModal(false)}
          onRemove={onDeleteAddress}
          cancelText="Hætta við"
          submitText="Eyða"
        />
      </Modal>
    </>
  )
}
